/**
 * L0 Acoustic Substrate — VoicePool (ARCHITECTURE.md §4).
 * Profiles are data; voices are rented lanes configured at trigger time. Spatial placement is
 * delegated to the active SpatialBackend (ambisonic field or per-voice panners — SPATIAL.md §6).
 * Imports Tone only; never reads the DOM.
 */
import * as Tone from 'tone'
import { clamp } from '../math/util'
import type { SonicProfile, SynthKind } from '../types'
import type { LaneOutput, SpatialBackend } from '../spatial/backend'
import { MatterVoice } from './matter-voice'

type AnySynth = Tone.Synth | Tone.FMSynth | Tone.PluckSynth | Tone.MembraneSynth | Tone.NoiseSynth | MatterVoice

interface Lane {
  kind: SynthKind
  synth: AnySynth
  filter: Tone.Filter
  out: LaneOutput
  busyUntil: number
}

export interface VoiceBuses {
  dryIn: Tone.InputNode
  wetIn: Tone.InputNode
}

export class VoicePool {
  private lanes: Lane[] = []
  private sleepTimer: ReturnType<typeof setInterval>

  constructor(
    private backend: SpatialBackend,
    private maxVoices: number,
  ) {
    this.maxVoices = clamp(maxVoices, 4, 24)
    // MODULAR.md §4 — idle matter lanes power down (silent page ⇒ ~zero steady-state CPU).
    this.sleepTimer = setInterval(() => {
      const now = Tone.now()
      for (const lane of this.lanes) {
        if (lane.synth instanceof MatterVoice && lane.busyUntil < now - 30) lane.synth.sleep()
      }
    }, 10000)
  }

  private createSynth(kind: SynthKind): AnySynth {
    switch (kind) {
      case 'matter':
        return new MatterVoice()
      case 'fm':
        return new Tone.FMSynth({ harmonicity: 3, modulationIndex: 8, envelope: { attack: 0.01, decay: 0.3, sustain: 0.1, release: 1.4 }, modulationEnvelope: { attack: 0.01, decay: 0.4, sustain: 0.2, release: 1 } })
      case 'pluck':
        return new Tone.PluckSynth({ attackNoise: 1, dampening: 3000, resonance: 0.92 })
      case 'membrane':
        return new Tone.MembraneSynth({ pitchDecay: 0.04, octaves: 5, envelope: { attack: 0.001, decay: 0.35, sustain: 0.01, release: 0.6 } })
      case 'noise':
        return new Tone.NoiseSynth({ noise: { type: 'white' }, envelope: { attack: 0.001, decay: 0.06, sustain: 0, release: 0.05 } })
      default:
        return new Tone.Synth({ oscillator: { type: 'triangle' }, envelope: { attack: 0.01, decay: 0.1, sustain: 0.25, release: 0.3 } })
    }
  }

  private createLane(kind: SynthKind): Lane {
    const synth = this.createSynth(kind)
    const filter = new Tone.Filter({ frequency: 4000, type: 'lowpass', rolloff: -12, Q: 1 })
    const out = this.backend.createOutput()
    synth.connect(filter)
    filter.connect(out.input)
    // The LFO's filter-wobble tap (MODULAR.md M4/M5) modulates the lane filter additively.
    if (synth instanceof MatterVoice) synth.modFilterOut.connect(filter.frequency)
    const lane: Lane = { kind, synth, filter, out, busyUntil: 0 }
    this.lanes.push(lane)
    return lane
  }

  private acquire(kind: SynthKind): Lane {
    const now = Tone.now()
    let candidate: Lane | null = null
    let oldestSameKind: Lane | null = null
    let oldestAny: Lane | null = null
    for (const lane of this.lanes) {
      if (lane.kind === kind) {
        if (lane.busyUntil <= now) { candidate = lane; break }
        if (!oldestSameKind || lane.busyUntil < oldestSameKind.busyUntil) oldestSameKind = lane
      }
      if (!oldestAny || lane.busyUntil < oldestAny.busyUntil) oldestAny = lane
    }
    if (candidate) return candidate
    if (this.lanes.length < this.maxVoices) return this.createLane(kind)
    if (oldestSameKind) return oldestSameKind
    // Cap reached and no lane of this kind: recycle the globally oldest lane into this kind.
    const victim = oldestAny ?? (this.lanes[0] as Lane)
    victim.synth.dispose()
    victim.synth = this.createSynth(kind)
    victim.synth.connect(victim.filter)
    victim.kind = kind
    return victim
  }

  /** Configure a lane from the profile, then sound it. `when` lets strums schedule ahead. */
  trigger(profile: SonicProfile, velocity: number, when?: number): void {
    const raw = velocity * profile.velocityScale
    if (raw <= 0.01) return // silent roles (e.g. mono theme text) stay silent
    const t = when ?? Tone.now()
    const vel = clamp(raw, 0.03, 1)
    const lane = this.acquire(profile.synthKind)

    const baseCutoff = Math.max(200, profile.filterHz)
    if (lane.kind === 'matter') {
      // EDGE bite: the filter strikes bright then settles (MATTER.md filter weave).
      const w = profile.voice
      lane.filter.Q.rampTo(w.filter.q, 0.02, t)
      lane.filter.frequency.cancelScheduledValues(t)
      lane.filter.frequency.setValueAtTime(Math.min(12000, baseCutoff * w.filter.biteAmount), t)
      lane.filter.frequency.exponentialRampTo(baseCutoff, w.filter.biteDecayS, t)
    } else {
      lane.filter.frequency.rampTo(baseCutoff, 0.02, t)
      lane.filter.Q.rampTo(profile.filterQ, 0.02, t)
    }
    lane.out.setPlacement(profile, t)

    const dur = profile.durationS
    try {
      switch (lane.kind) {
        case 'matter': {
          const mv = lane.synth as MatterVoice
          mv.trigger({
            freqHz: profile.freqHz,
            velocity: vel,
            durationS: dur,
            releaseScaleBase: profile.release / 0.3,
            voice: profile.voice,
          }, t, baseCutoff)
          lane.busyUntil = t + dur + mv.releaseTail()
          return
        }
        case 'noise': {
          ;(lane.synth as Tone.NoiseSynth).triggerAttackRelease(Math.min(dur, 0.2), t, vel)
          break
        }
        case 'pluck': {
          const pluck = lane.synth as Tone.PluckSynth
          pluck.set({ dampening: clamp(profile.filterHz, 400, 7000) })
          pluck.triggerAttackRelease(profile.freqHz, dur, t, vel)
          break
        }
        case 'membrane': {
          const mem = lane.synth as Tone.MembraneSynth
          mem.set({ envelope: { attack: Math.max(0.001, profile.attack / 4), release: profile.release } })
          mem.triggerAttackRelease(Math.max(30, profile.freqHz / 4), dur, t, vel)
          break
        }
        case 'fm': {
          const fm = lane.synth as Tone.FMSynth
          fm.set({ envelope: { attack: profile.attack, release: profile.release * 2 } })
          fm.triggerAttackRelease(profile.freqHz, dur, t, vel)
          break
        }
        default: {
          const syn = lane.synth as Tone.Synth
          syn.set({
            oscillator: { type: profile.wave },
            envelope: { attack: profile.attack, decay: 0.08, sustain: 0.25, release: profile.release },
          })
          syn.triggerAttackRelease(profile.freqHz, dur, t, vel)
        }
      }
      lane.busyUntil = t + dur + profile.release
    } catch (err) {
      console.warn('[sonarium] trigger failed', err)
    }
  }

  get activeCount(): number {
    const now = Tone.now()
    return this.lanes.filter((l) => l.busyUntil > now).length
  }

  /** MODULAR.md §3 — a sustained ribbon voice. Returns null when no matter lane can gate. */
  sustain(profile: SonicProfile, velocity: number): { setFreq(hz: number, glideS: number): void; release(): void } | null {
    const raw = velocity * profile.velocityScale
    if (raw <= 0.01) return null
    const lane = this.acquire('matter')
    if (!(lane.synth instanceof MatterVoice)) return null
    const mv = lane.synth
    const t = Tone.now()
    const baseCutoff = Math.max(200, profile.filterHz)
    lane.filter.Q.rampTo(profile.voice.filter.q, 0.02, t)
    lane.filter.frequency.cancelScheduledValues(t)
    lane.filter.frequency.setValueAtTime(Math.min(12000, baseCutoff * profile.voice.filter.biteAmount), t)
    lane.filter.frequency.exponentialRampTo(baseCutoff, profile.voice.filter.biteDecayS, t)
    lane.out.setPlacement(profile, t)
    mv.gateOn({
      freqHz: profile.freqHz,
      velocity: clamp(raw, 0.03, 1),
      durationS: 9999,
      releaseScaleBase: profile.release / 0.3,
      voice: profile.voice,
    }, t, baseCutoff)
    lane.busyUntil = t + 9999
    const voice = profile.voice
    return {
      setFreq(hz: number, glideS: number) {
        mv.setFreq(hz, glideS, voice.subShimmer.interval, voice.patch.fm.index)
      },
      release() {
        const tail = mv.gateOff()
        lane.busyUntil = Tone.now() + tail
      },
    }
  }

  dispose(): void {
    clearInterval(this.sleepTimer)
    for (const lane of this.lanes) {
      lane.synth.dispose()
      lane.filter.dispose()
      lane.out.dispose()
    }
    this.lanes = []
  }
}
